// src/hooks/useUsageTracking.ts (Fixed)
import { useEffect, useMemo } from 'react';
import { useLocalStorage } from './useLocalStorage';
import type { UsageState } from '@/types/usage';

const FREE_USAGE_LIMIT = 2;

export const useUsageTracking = (isAuthenticated: boolean) => {
  const [usageCount, setUsageCount] = useLocalStorage('usageCount', 0);
  
  // Memoize usage state to prevent unnecessary re-renders
  const usageState = useMemo<UsageState>(() => {
    if (isAuthenticated) {
      return {
        count: 0,
        limit: Infinity,
        isAuthenticated: true
      };
    }
    
    return {
      count: usageCount,
      limit: FREE_USAGE_LIMIT,
      isAuthenticated: false
    };
  }, [isAuthenticated, usageCount]);

  // Reset usage when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && usageCount > 0) {
      setUsageCount(0);
    }
  }, [isAuthenticated, usageCount, setUsageCount]);

  const incrementUsage = () => {
    if (!isAuthenticated) {
      setUsageCount(prev => prev + 1);
    }
  };

  const resetUsage = () => {
    setUsageCount(0);
  };

  const canMakeRequest = isAuthenticated || usageCount < FREE_USAGE_LIMIT;
  const remainingCalls = isAuthenticated ? Infinity : Math.max(0, FREE_USAGE_LIMIT - usageCount);

  return {
    usageState,
    incrementUsage,
    resetUsage,
    canMakeRequest,
    remainingCalls
  };
};